import React, { useEffect, useMemo, useState } from "react";
import _ from "lodash";

import { compose, graphql } from "lib/apolloClient";
import { connect } from "react-redux";

import withLoader from "UIComponents/GqlWithLoader";

import DatasetTaggingBody from "./DatasetTaggingBody";
import DatasetSideNavigation from "./components/DatasetsSideNavigation";
import { FilterConsumer, FilterProvider } from "./providers/FilterProvider";
import {
  getRootNodesFromNodeSetQuery,
  getRootNodesFromSetQuery,
} from "./modules/Queries";
import { getPlannerElementConfigurations } from "./modules/Utils";
import useQueryVariables from "./hooks/useQueryVariables";
import classes from "./DatasetTaggingBodyWrapper.scss";

const EMPTY_OBJECT = {};
const EMPTY_ARRAY = [];

/**
 * DatasetsTaggingBodyWrapper - Container component for dataset tagging functionality
 * Manages side navigation, filters, columns configuration and communication between child components
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.plannerElementType - Type of planner element being tagged (e.g., 'STANDARD', 'DATASET')
 * @param {string} props.plannerElementNodeSetId - ID of the planner element node set to be tagged/displayed
 * @param {string} props.plannerElementSetId - ID of the planner element set to be tagged/displayed,
 * if this is passed ensure it has only one nodeSet, like for non standards planner elements, but if a set has multiple
 * either pass setId or nodeSetId
 * @param {Array} drops.selectedRows - Array of selected row IDs/objects from the tagging grid
 * @param {Function} props.updateSelectedRows - Callback function to update the selected rows when user selects/deselects items
 * @param {Array} props.defaultColumns - Default column configuration for the tagging body if this is not passed default columns is shown based on getCurrentColumnsUtil function
 * @param {Object} props.config - Configuration object controlling tagging behavior and restrictions, ensure this is memoized
 * @param {string} props.config.moduleType - Type of module (e.g., 'LearningCourse', 'JOURNAL', 'CLASSROOM').
 *   Used to determine module-specific tagging rules and available columns
 * @param {boolean} [props.config.disableUntaggingIfTaggedInOtherEntities] - Prevents untagging operations
 *   when the item is tagged in other modules. Ensures data consistency across the platform
 * @param {Object} [props.initialFilters] - Initial filter values to apply on component mount.
 *   Includes subtypes, archival state, and other filter criteria. It takes all the filters which are
 *   accepted by PlannerElementNodeFilters. This is used to initial preselected filters for filter dropdown,
 *   and it can be overridden from that dropdown
 * @param {Object} [props.dynamicFilters={}] - Dynamic filters that change based on user interaction.
 *   This contains filters which can be changed from user interaction in parent component, like toggle in
 *   header to show only nodes tagged in course
 * @param {Object} [props.nodeCountFilters] - Filters for counting nodes, takes all input for dataSetNodeCountWrapperFilters
 *   (e.g., `{ learningCourseIds: [courseId] }`). Used to track what's tagged in specific contexts
 * @param {Object} [props.nodeSetFilters={}] - Filters specifically for node sets. Can override initialFilters
 *   for specific filtering scenarios
 * @param {Function} [props.actionHandlerFn] - callback function to  handle user actions while clicking on table cell, used for opening modals in parent for tagged in columns.
 * @param {boolean} [props.showSideNavigation=false] - Whether to display the side navigation panel.
 *   Only shown if multiple root nodes or parent nodes exist. This is used for other learning goals
 *   where we show side navigation
 * @param {Object} [props.parentNodesData={}] - Object containing parent nodes data for hierarchy display. Ensure this is memoized
 *   For some planner element types, we need parentNodesData from parent, like subjects in case of related concepts
 * @param {Array} props.parentNodesData.parentNodes - Array of parent node objects streamed from parent
 * @param {Array} [props.parentNodesData.entityTypes] - Identifies the type of parent nodes. Should take value
 *   that can be passed to nodesFilters. For plannerElements with multiple NodeSets like standards,
 *   it will be passed in nodeSetFilters
 * @param {Array} [props.allowedColumns=[]] - Columns allowed to be displayed/configured in the tagging body.
 *   Other columns will be hidden from user selection
 */
const DatasetsTaggingBodyWrapper = props => {
  const {
    defaultColumns: _defaultColumns,
    config: { moduleType, disableUntaggingIfTaggedInOtherEntities = true },
    initialFilters,
    dynamicFilters: _dynamicFilters = EMPTY_OBJECT,
    academicSetupType,
    plannerElementType,
    showSideNavigation = false,
    parentNodesData = EMPTY_OBJECT,
    rootEdges,
    allowedColumns = EMPTY_ARRAY,
    nodeSetFilters: _nodeSetFilters = EMPTY_OBJECT,
    plannerElementConfig,
  } = props;

  const [parentNodesCount, setParentNodesCount] = useState({});
  const { parentNodes } = parentNodesData;
  const { shouldUseParentNodes } = plannerElementConfig;
  const rootNodes = useMemo(() => {
    return shouldUseParentNodes ? parentNodes : _.map(rootEdges, "node");
  }, [rootEdges, parentNodes, shouldUseParentNodes]);

  const preSelectedId = _.first(rootNodes)?.id;
  const [sideNavigationTab, setSideNavigationTab] = useState(preSelectedId);
  useEffect(() => {
    setSideNavigationTab(preSelectedId);
  }, [preSelectedId]);

  const {
    currentColumns,
    defaultColumns,
    columnIdsForConfigureModal,
    updateColumns,
    skipFlags,
    dynamicFilters,
    nodeSetFilters,
  } = useQueryVariables({
    defaultColumns: _defaultColumns,
    academicSetupType,
    moduleType,
    plannerElementType,
    disableUntaggingIfTaggedInOtherEntities,
    sideNavigationTab,
    parentNodesData,
    dynamicFilters: _dynamicFilters,
    nodeSetFilters: _nodeSetFilters,
    allowedColumns,
  });

  const shouldShowSideNavigation =
    showSideNavigation &&
    ((shouldUseParentNodes && _.size(parentNodes) > 1) ||
      _.size(rootNodes) > 1);

  return (
    <FilterProvider
      initialFilters={initialFilters}
      dynamicFilters={dynamicFilters}
    >
      <FilterConsumer>
        {({ filters, initialFilterVariables }) => {
          return (
            <div className={classes.wrapperBody}>
              {shouldShowSideNavigation ? (
                <DatasetSideNavigation
                  {...props}
                  rootNodes={rootNodes}
                  sideNavigationTab={sideNavigationTab}
                  handleOptionClick={setSideNavigationTab}
                  parentNodesCount={parentNodesCount}
                  setParentNodesCount={setParentNodesCount}
                  shouldShowSideNavigation={shouldShowSideNavigation}
                />
              ) : null}
              <DatasetTaggingBody
                {...props}
                currentColumns={currentColumns}
                defaultColumns={defaultColumns}
                updateColumns={updateColumns}
                columnIdsForConfigureModal={columnIdsForConfigureModal}
                skipFlags={skipFlags}
                nodesFilters={filters}
                nodeSetFilters={nodeSetFilters}
                setParentNodesCount={setParentNodesCount}
                sideNavigationTab={sideNavigationTab}
                shouldShowSideNavigation={shouldShowSideNavigation}
                initialFilterVariables={initialFilterVariables}
              />
            </div>
          );
        }}
      </FilterConsumer>
    </FilterProvider>
  );
};

const mapStateToProps = (state, ownProps) => {
  const { plannerElementType } = ownProps;
  const academicSetupType = _.get(
    state,
    "platform.currentCurriculumProgram.academicSetupType",
    null
  );
  const plannerElementConfig = getPlannerElementConfigurations({
    plannerElementType,
  });
  return { academicSetupType, isData: true, plannerElementConfig };
};

export default compose(
  connect(mapStateToProps),
  graphql(getRootNodesFromNodeSetQuery, {
    name: "getRootNodesFromNodeSet",
    skip: ({
      showSideNavigation: _showSideNavigation,
      parentNodesData = EMPTY_OBJECT,
      plannerElementConfig: { showSideNavigation, shouldUseParentNodes },
      plannerElementNodeSetId,
    }) => {
      const { parentNodes } = parentNodesData;
      return (
        !plannerElementNodeSetId ||
        !_showSideNavigation ||
        !showSideNavigation ||
        (shouldUseParentNodes && !_.isEmpty(parentNodes))
      );
    },
    options: ({
      plannerElementNodeSetId,
      initialFilters,
      dynamicFilters,
      plannerElementType,
    }) => {
      let depth = 1;
      if (_.includes(initialFilters.subTypes, "INTERDISCIPLINARY_CRITERIA")) {
        const { subTypeRootDepth } = getPlannerElementConfigurations({
          plannerElementType,
        });
        depth = subTypeRootDepth;
      }

      return {
        fetchPolicy: "cache-and-network",
        variables: {
          id: plannerElementNodeSetId,
          nodesFilters: {
            depth,
            archivalState: "ALL",
            ...initialFilters,
            ...dynamicFilters,
          },
        },
      };
    },
    props: ({ getRootNodesFromNodeSet }) => {
      const { node, loading } = getRootNodesFromNodeSet;
      const rootEdges = _.get(
        node,
        "plannerElementNodeSetWrapperV2.publishedData.edges",
        []
      );
      return {
        rootEdges,
        isData: !_.isEmpty(node),
        isLoading: loading,
      };
    },
  }),
  graphql(getRootNodesFromSetQuery, {
    name: "getRootNodesFromSet",
    skip: ({
      showSideNavigation: _showSideNavigation,
      parentNodesData = EMPTY_OBJECT,
      plannerElementConfig: { showSideNavigation, shouldUseParentNodes },
      plannerElementSetId,
    }) => {
      const { parentNodes } = parentNodesData;
      return (
        !plannerElementSetId ||
        !_showSideNavigation ||
        !showSideNavigation ||
        (shouldUseParentNodes && !_.isEmpty(parentNodes))
      );
    },
    options: ({
      plannerElementSetId,
      initialFilters,
      dynamicFilters,
      plannerElementType,
    }) => {
      let depth = 1;
      if (_.includes(initialFilters.subTypes, "INTERDISCIPLINARY_CRITERIA")) {
        const { subTypeRootDepth } = getPlannerElementConfigurations({
          plannerElementType,
        });
        depth = subTypeRootDepth;
      }

      return {
        fetchPolicy: "cache-and-network",
        variables: {
          id: plannerElementSetId,
          nodesFilters: {
            depth,
            archivalState: "ALL",
            ...initialFilters,
            ...dynamicFilters,
          },
        },
      };
    },
    props: ({ getRootNodesFromSet }) => {
      const { node, loading } = getRootNodesFromSet;
      const rootEdges = _.get(
        node,
        "plannerElementNodeSets.0.plannerElementNodeSetWrapperV2.publishedData.edges",
        []
      );
      return {
        rootEdges,
        isData: !_.isEmpty(node),
        isLoading: loading,
      };
    },
  }),
  withLoader
)(DatasetsTaggingBodyWrapper);
